/**
 * Single data hook behind every Home live surface (design spec §5.1, §7; Live Layer v2).
 *
 * Fetches the upcoming feed across the user's joined spaces (GET /events/my-calendars)
 * with one plain `useQuery`, then partitions the result client-side into:
 *   - `entries`      the ALWAYS-ON rail — every upcoming/soon/live event across
 *                    the member's joined spaces. ORDER: see `compareEntries`
 *                    below, which is where the rule, its rationale and its
 *                    lineage live. Every entry comes from ONE source, `GET
 *                    /events/my-calendars`, so the not-going entries are events
 *                    in spaces the member has already JOINED and not RSVP'd to —
 *                    not public discovery (that only exists on the Meetverse
 *                    row, which merges a second source in). The Home pill row
 *                    renders them ALL as a swipe row (recap/pill redesign §1; the
 *                    width rule is the overflow mechanism). v2 keeps `upcoming` —
 *                    the member always sees the next event(s), however far out —
 *                    and retires the v1 goers-only-live rule: a live entry is a
 *                    pill for EVERY member, not only goers (the carousel ring is
 *                    an additional cue, no longer the sole one).
 *   - `liveSpaceIds` primary space_id of the LIVE entries (strict live-only; drives carousel rings)
 *   - `now`          the tick the phases were derived against (consumers reuse it — one clock)
 *
 * Phases are derived with `getEventPhase` against a `useNow` tick, so a phase flip appears within
 * one tick without re-fetching; the tick (and the query) are gated on the `liveEvents` kill switch
 * AND authentication (`getMyCalendars` 401s for guests), so a disabled/guest mount runs zero
 * timers and zero requests. Consumers should call this from an isolated leaf (the pill/carousel
 * wrapper), NOT at feed level, so the tick does not re-render the feed (spec §3).
 *
 * Identity contract: ticks that change no entry (same events, same phases, same order) return the
 * SAME `entries`/`liveSpaceIds`/`liveEventBySpaceId` references, so identity-keyed
 * consumers (`SpacesCarousel`'s ordering memo, its ring-impression effect) don't churn every 30s.
 * A genuine transition (phase flip, membership change, reorder) hands out fresh references —
 * see `useStableBySignature`.
 *
 * The rail intentionally has NO recap output: the endpoint's `pov=upcoming` drops ended events
 * (`end_date >= now`), so anything ended is only in the response transiently. The reliable
 * ended-recap source is `useLiveRecaps` (`GET /events/my-recaps`).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { featureFlags } from '@config/featureFlags';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { liveRailQueryOptions } from '@hooks/liveQueries';
import { previewUserSignature } from '@hooks/useMeetverseLiveEvents';
import { useNow } from '@hooks/useNow';
import { useStableBySignature } from '@hooks/useStableBySignature';
import type { EventRead } from '@models/api';
import { useAuth } from '@providers';
import { getEventGoingCount } from '@utils/eventGoingCount';
import { getEventPhase, startMsOrInfinity } from '@utils/eventLiveness';
import { isEventCancelled } from '@utils/eventStatus';

export interface LiveRailEntry {
  event: EventRead;
  phase: 'upcoming' | 'soon' | 'live';
}

export interface UseLiveEventsRailResult {
  /**
   * The always-on rail: every upcoming/soon/live event across the member's
   * joined spaces. Order: see `compareEntries`. This hook's own output has no
   * guest case to speak of: it is auth-gated (`enabled` requires
   * `isAuthenticated`), so a guest mount always returns the empty rail before any
   * sort ever runs — the guest-collapse case belongs to
   * `LiveMeetversePills`'/`useMeetverseLiveEvents`'s public path, where it is
   * already documented and pinned. v2 has no eligibility filter — a live entry is
   * a pill for every member (the v1 goers-only-live rule is retired) and
   * `upcoming` stays in. Empty when the member has no upcoming/soon/live event.
   */
  entries: LiveRailEntry[];
  /** Primary `space_id` of entries whose phase is LIVE — strict live-only (spec §5.1 rings). */
  liveSpaceIds: Set<string>;
  /**
   * Primary `space_id` → its live event (spec §7's "spaceId → liveEvent map").
   * Same live-only partition as `liveSpaceIds` (which is derived from this
   * map's keys); with several simultaneous live events in one space the first
   * in feed order (earliest start, per the endpoint's sort) wins. Feeds the
   * carousel-ring analytics payloads.
   */
  liveEventBySpaceId: Map<string, EventRead>;
  /**
   * The tick the partition was derived against. Consumers that render
   * time-relative copy (the pill's countdown) MUST reuse this instead of
   * mounting a second `useNow` — one clock per surface tree (W-9).
   */
  now: Date;
}

/** The identity-stabilized slice of the result (everything except the ticking `now`). */
type RailPartition = Omit<UseLiveEventsRailResult, 'now'>;

/**
 * Frozen module-level empty partition: the disabled / guest / loading / empty
 * paths return one stable identity for free (never a fresh empty per render).
 */
const EMPTY_RAIL: RailPartition = Object.freeze({
  entries: [],
  liveSpaceIds: new Set<string>(),
  liveEventBySpaceId: new Map<string, EventRead>(),
});

const isGoing = (event: EventRead): boolean => event.is_going === true;

/**
 * Phase severity: live=0, soon=1, upcoming=2. Lower rank sorts first.
 *
 * Compiler-exhaustive by construction: `Record<LiveRailEntry['phase'], number>`
 * means a future fourth phase is a TYPE ERROR at this literal (a missing
 * property), not a silent mis-rank via an if/else's implicit final `else`.
 */
const PHASE_SEVERITY_RANK: Record<LiveRailEntry['phase'], number> = {
  live: 0,
  soon: 1,
  upcoming: 2,
};
const phaseRank = (phase: LiveRailEntry['phase']): number => PHASE_SEVERITY_RANK[phase];

/**
 * The going key: going=0, not-going=1. Lower rank sorts first.
 *
 * SHARED by both comparators on purpose (PR #1044 review): the expression was
 * duplicated inline in each, so a later refinement — ranking a host above a
 * plain goer, or giving `is_going === null` its own tier — could land on
 * `compareEntries` alone and silently leave the Meetverse merged row on the old
 * rule, rendering the same two events in opposite orders one tab tap apart with
 * no compile error.
 */
const goingRank = (event: EventRead): number => (isGoing(event) ? 0 : 1);

/**
 * THE ordering rule for every live surface (Dexter's ruling, 2026-07-29, after
 * using the shipped rail in production): PHASE SEVERITY first — live > soon >
 * upcoming — then GOING, then the earliest start.
 *
 * The rationale is the rail's purpose: "I want it to also act as discovery and
 * reminders … show live > soon > upcoming events from all my joined spaces …
 * The going acts as a tie breaker … the goal is to make users discover the
 * events and remember to check them." An event that is live or about to start
 * must be visible even to a member who is NOT going to it — that member is
 * exactly who the reminder is for, and burying such a room under the member's
 * own far-future RSVPs is what made it invisible in production ("the events I
 * am going to [come] first before the starting soon events, so I can't see
 * these events unless I scroll and users will probably miss it").
 *
 * Going is therefore a SAME-PHASE tiebreak only: it can never move an entry
 * across a phase boundary. Guests (nothing is ever `is_going`) collapse to pure
 * phase severity, byte-identically.
 *
 * (Supersedes the 2026-07-28 six-bucket agenda table, which superseded
 * going-first.)
 *
 * WHO RUNS THIS COMPARATOR, and the three places behavior still differs
 * (corrected PR #1044 review — the previous enumeration was wrong on both its
 * count and its members):
 *   - the HOME pill row and the space page's cell order run it directly;
 *   - the MEETVERSE merged row runs the stable variant below instead, so a
 *     second source's deliberate server ranking survives the merge;
 *   - the SPACE PAGE pins its ended-recap cell to index 0, ahead of every
 *     upcoming cell (`pickSlotCells`' recap branch) — a display rule this
 *     comparator cannot express;
 *   - RING DERIVATION DOES NOT RUN IT AT ALL. `liveEventBySpaceId` is filled
 *     first-wins inside the pre-sort payload loop below, so the rings consume
 *     `my-calendars`' own start-ASC server order: for two concurrently-live
 *     events in one space the EARLIER-STARTED one wins the ring, going or not.
 *     Documented so nobody "consolidates rings onto the one rule" believing it
 *     is a no-op — that would be a behavior change, and whether the ring should
 *     prefer the viewer's going event is an open product question.
 */
export const compareEntries = (a: LiveRailEntry, b: LiveRailEntry): number => {
  const phaseDelta = phaseRank(a.phase) - phaseRank(b.phase);
  if (phaseDelta !== 0) {
    return phaseDelta;
  }
  const goingDelta = goingRank(a.event) - goingRank(b.event);
  if (goingDelta !== 0) {
    return goingDelta;
  }
  return startMsOrInfinity(a.event) - startMsOrInfinity(b.event);
};

/**
 * The STABLE variant of the rule above (Dexter's "Keep server ranking" ruling,
 * 2026-07-28, fresh-eyes review finding I3): phase, then going, and NOTHING
 * else — two entries sharing a phase AND a going value compare EQUAL, so a
 * stable sort leaves them exactly where they were.
 *
 * Built from the SAME `phaseRank` AND `goingRank` helpers `compareEntries` uses
 * above, minus its start tiebreak — neither key can drift between the two.
 *
 * REACHABILITY (PR #1044 review, and why the going key is unit-pinned rather
 * than row-pinned): in today's row pipeline this comparator's going key is
 * normally UNREACHABLE. `personalEntries` arrives already `compareEntries`-sorted
 * (going first inside each phase) and the display cap only drops, while every
 * `publicOnly` entry is `is_going === false` by the own-event filter — so the
 * concatenation is already (phase, going)-sorted before the sort runs, and the
 * row's "your own pills lead their phase" property rests on concat order plus
 * sort stability. The key is the GUARD for when either invariant moves (flip the
 * concat order, or let a going entry into the public block, and it is suddenly
 * the only thing holding the property up). That is exactly why it is pinned
 * directly, at the unit level, in `useLiveEventsRail.test.tsx` — a row-level
 * fixture cannot reach it and would quietly pass with the key deleted.
 *
 * WHO USES THIS AND WHY: `LiveMeetversePills` sorts its cross-source merged row
 * (personal rail entries + public discovery entries) with THIS comparator, not
 * the full `compareEntries`. The two sources are each independently ranked by
 * their own backend call, and the public discovery order is DELIBERATE (busiest
 * room first for live rows) — running the start tiebreak across both would
 * launder that server ranking through a client start-time sort. This comparator
 * trusts each source's own pre-sort order for everything except the phase and
 * going boundaries: within one (phase, going) group personal entries lead in
 * the rail's own order (already `compareEntries`-sorted upstream), then public
 * entries fill the rest in their payload's own order. Public entries are
 * `is_going === false` by construction (the row's own-event filter drops any
 * that aren't), so they only ever join a not-going group — but those groups MIX
 * both sources, since the member's own not-going joined-space entries land
 * there too: "the not-going group" must never be read as a synonym for "the
 * discovery rows".
 */
export const compareEntriesByPhaseAndGoingOnly = (a: LiveRailEntry, b: LiveRailEntry): number => {
  const phaseDelta = phaseRank(a.phase) - phaseRank(b.phase);
  if (phaseDelta !== 0) {
    return phaseDelta;
  }
  return goingRank(a.event) - goingRank(b.event);
};

/** Per-phase display bounds for `capRailForDisplay`; omit a phase to leave it uncapped. */
export interface RailDisplayCaps {
  /** Max LIVE entries. Omitted = uncapped (the Meetverse discovery row). */
  liveMax?: number;
  /** Max UPCOMING entries. Omitted = uncapped. */
  upcomingMax?: number;
}

/**
 * THE display shaper for rail surfaces — ONE pass, one place, instead of a stack
 * of bespoke per-phase passes whose composition order had to be reasoned about
 * (PR #1044 review). Never reorders: the surviving entries keep the exact order
 * they arrived in, which is `compareEntries` order.
 *
 * The FETCH stays wider (`LIVE_RAIL_PER_PAGE`) and the hook's own partition stays
 * uncapped on purpose, so ring derivation (`liveSpaceIds` / `liveEventBySpaceId`),
 * the ordering and the identity signature all see everything the payload carried;
 * only the RENDERED row trims. Call it at the render layer (`LiveHomePill`,
 * `LiveMeetversePills`' personal block), never inside the hook.
 *
 * The two caps use DIFFERENT eligibility rules, deliberately — each answers the
 * failure its own phase actually has:
 *
 *   LIVE (`liveMax`, Dexter's ruling 2026-07-29, HOME ONLY): keep the first
 *   `liveMax` in the order given. Order is `compareEntries`, so the viewer's own
 *   going-live entries lead the live block and the trim eats into the NOT-GOING
 *   tail FIRST — but only until the going-live entries alone reach `liveMax`.
 *   With 4+ concurrent going-live events the cap trims the member's own rooms
 *   too, by start; nothing is exempt from a display bound. Bounds the run length
 *   without touching the order: eight joined-space rooms live at once used to
 *   push the member's own 20-minutes-away RSVP to slot 9, ~8 swipes off-screen at
 *   90vw per slot, so the reminder this surface exists to deliver was never
 *   seen.
 *
 *   UPCOMING (`upcomingMax`, Dexter's ruling 2026-07-29): keep the `upcomingMax`
 *   NEAREST by start, REGARDLESS of going — eligibility is start, not going.
 *   Going stays a DISPLAY-order key only (the survivors still render in
 *   `compareEntries` order, going first). Under the previous going-first trim the
 *   going key stopped being a tiebreak and became an eligibility filter: three
 *   RSVPs 4-9 days out evicted a joined-space room starting in 3 hours (phase
 *   `upcoming`, since `soonLeadMs` is 2h) from the row entirely — re-creating one
 *   phase below exactly the "users will probably miss it" burial this whole rule
 *   was made to fix.
 */
export function capRailForDisplay(
  entries: readonly LiveRailEntry[],
  { liveMax, upcomingMax }: RailDisplayCaps,
): LiveRailEntry[] {
  // Upcoming eligibility is by NEAREST START, which the incoming order does not
  // give us (it is going-first within the phase), so the surviving ids are
  // chosen on a start-sorted copy and then filtered OUT OF THE ORIGINAL ORDER —
  // eligibility and display order stay independent.
  const keptUpcomingIds =
    upcomingMax === undefined
      ? null
      : new Set(
          entries
            .filter(entry => entry.phase === 'upcoming')
            .slice()
            .sort((a, b) => startMsOrInfinity(a.event) - startMsOrInfinity(b.event))
            .slice(0, upcomingMax)
            .map(entry => entry.event.id),
        );

  const shaped: LiveRailEntry[] = [];
  let liveKept = 0;
  for (const entry of entries) {
    if (entry.phase === 'live' && liveMax !== undefined) {
      if (liveKept >= liveMax) {
        continue;
      }
      liveKept += 1;
    }
    if (
      entry.phase === 'upcoming' &&
      keptUpcomingIds !== null &&
      !keptUpcomingIds.has(entry.event.id)
    ) {
      continue;
    }
    shaped.push(entry);
  }
  return shaped;
}

export function useLiveEventsRail(): UseLiveEventsRailResult {
  const { isAuthenticated } = useAuth();
  // Auth is part of the gate (N-3): my-calendars is auth-only (401 for guests). Today every
  // mount site is auth-gated, but the hook must not rely on that staying true.
  const enabled = featureFlags.liveEvents && isAuthenticated;
  // Primitive tick value drives phase recomputation without a non-primitive dep (house rule).
  // `enabled` reaches the tick too: the kill switch stops the rail timer, not just the fetch.
  const nowMs = useNow(LIVE_EVENTS_CONFIG.nowTickMs, enabled).getTime();

  // Shared options (`liveQueries`) keep this producer byte-identical with the
  // space-page selector's; refetch-on-focus intentionally stays at the house
  // default (true) — spec §7.
  const { data: events } = useQuery({ ...liveRailQueryOptions(), enabled });

  const { partition, signature } = useMemo(() => {
    if (!enabled || !events || events.length === 0) {
      return { partition: EMPTY_RAIL, signature: '' };
    }

    const now = new Date(nowMs);
    const entries: LiveRailEntry[] = [];
    const liveEventBySpaceId = new Map<string, EventRead>();

    for (const event of events) {
      // A cancelled event is never a Live / Starting soon pill or ring (TECH-324): my-calendars
      // still returns it, and there is nothing to join.
      if (isEventCancelled(event)) continue;
      const phase = getEventPhase(event, now);
      // v2: the rail spans upcoming/soon/live — the always-on band. `liveSpaceIds`
      // / `liveEventBySpaceId` stay strict live-only (rings), derived below.
      if (phase === 'live' || phase === 'soon' || phase === 'upcoming') {
        entries.push({ event, phase });
        if (phase === 'live' && event.space_id && !liveEventBySpaceId.has(event.space_id)) {
          liveEventBySpaceId.set(event.space_id, event);
        }
      }
    }

    if (entries.length === 0) {
      return { partition: EMPTY_RAIL, signature: '' };
    }

    entries.sort(compareEntries);

    return {
      partition: {
        entries,
        liveSpaceIds: new Set(liveEventBySpaceId.keys()),
        liveEventBySpaceId,
      },
      // Everything selection- or copy-relevant, in order: id set + order, phase
      // (rings + pill status cue), is_going (the same-phase order tiebreak, and
      // the goer/non-goer copy), space_id (ring set/map keys), going count (pill "+N going" copy),
      // preview identity (id + name + picture; the pill v2 goer stack renders these
      // avatars and names, so a refetch that changes any preview field, even with the
      // same id set, must hand out fresh references), start_date (the soon/upcoming
      // pill's countdown/date copy), name (the pill label + ring analytics payloads — encoded
      // so free text can't collide with the field/entry separators; #967 review:
      // an unsigned rendered field silently stops updating mid-live). Ticks (or
      // refetches) that change none of these keep the previous identities.
      signature: entries
        .map(
          e =>
            `${e.event.id}:${e.phase}:${e.event.is_going}:${e.event.space_id}:${getEventGoingCount(e.event)}:${(e.event.preview_users ?? []).map(previewUserSignature).join(',')}:${e.event.start_date}:${encodeURIComponent(e.event.name)}`,
        )
        .join('|'),
    };
  }, [enabled, events, nowMs]);

  const stable = useStableBySignature(partition, signature);
  // One Date per tick (not per consumer render) — the W-9 shared clock.
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  return useMemo(
    () => ({
      entries: stable.entries,
      liveSpaceIds: stable.liveSpaceIds,
      liveEventBySpaceId: stable.liveEventBySpaceId,
      now,
    }),
    [stable, now],
  );
}
